import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Chip, CircularProgress, Container, Typography } from '@mui/material';

import PageTitle from '../../components/PageTitle/PageTitle';
import ToggleButtonContainer from '../../components/ToggleButton/ToggleButtonContainer';
import { enableShopifyVenueID } from '../../config';
import useUrlQuery from '../../hooks/useUrlQuery';
import useVenueId from '../../hooks/useVenueId';
import { OfferCategory } from '../../graphql/API';
import { useAppSelector } from '../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { getCycleOptions } from './common/helper';
import AlertMessage from './components/AlertMessage/AlertMessage';
import Plans from './components/Plans/Plans';
import SubscriptionDetail from './components/SubscriptionDetail/SubscriptionDetail';
import { useSubscription } from './context/Subscription.context';
import SubscriptionPrivider from './context/Subscription.provider';
import { BillingCycle, Plan } from './types';

const PlanWithDiscount: React.FC<{ label: string; discount?: number }> = ({ label, discount }) => (
  <Box display='flex' alignItems='baseline'>
    {label}
    {discount && (
      <Typography variant='caption' sx={{ marginLeft: 1, color: 'red', fontWeight: 500 }}>
        -{discount}% off
      </Typography>
    )}
  </Box>
);

const SubscriptionPlans: React.FC = () => {
  const q = useUrlQuery();
  const updatedPlan = q.get('updatedPlan') as Plan | undefined;

  const { isLoading, pricing, subscription } = useAppSelector((state) => state.subscription.value);
  const { tags: venueTags, type: venueType } = useAppSelector((state) => state.venue.value) || {};

  const { state, dispatch } = useSubscription();

  const cycleOptions = React.useMemo(() => {
    if (!pricing) {
      return [];
    }

    const options = getCycleOptions(state.plan, pricing);

    return options.map((x) => ({
      label: <PlanWithDiscount label={x.label} discount={x.discount} />,
      value: x.value,
    }));
  }, [state.plan, pricing]);

  const toggleChangeHandler = React.useCallback(
    (v: string) => {
      dispatch({ type: 'update-state', payload: { billingCycle: v as BillingCycle } });
    },
    [dispatch],
  );

  return (
    <Box
      sx={{
        flex: 1,
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FEFEFE',
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Container maxWidth='lg'>
          <PageTitle
            titleComponent={
              <Box display='flex' alignItems='center' gap='10.5px'>
                <Typography fontSize={'28px'} fontWeight={500}>
                  Plans
                </Typography>
                {venueType === OfferCategory.beauty && !venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && (
                  <Chip
                    label='Beauty Venue'
                    size='small'
                    variant='outlined'
                    sx={{
                      height: '31px',
                      borderRadius: '8px',
                      borderColor: '#E6E6E6',
                      backgroundColor: '#FAFAFA',
                      px: '14px',
                      py: '4px',
                      '& .MuiChip-label': {
                        padding: 0,
                        fontSize: '15px',
                        fontWeight: 400,
                        color: '#111111',
                        lineHeight: '100%',
                      },
                    }}
                  />
                )}
              </Box>
            }
            rightComponent={
              <>
                {!venueTags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && (
                  <ToggleButtonContainer
                    borderRadius={4}
                    buttonColour='black'
                    buttons={cycleOptions}
                    value={state.billingCycle}
                    onChange={toggleChangeHandler}
                  />
                )}
              </>
            }
            sx={{
              fontSize: '32px',
            }}
          />
          <AlertMessage plan={updatedPlan || state.plan} />
          <Box sx={{ marginTop: 4 }}>
            <Plans />
          </Box>
          {subscription && (
            <Box sx={{ marginTop: 4 }}>
              <PageTitle title='Manage your plan' />
              <Box marginTop={2}>
                <SubscriptionDetail subscription={subscription} pricing={pricing ?? []} />
              </Box>
            </Box>
          )}
        </Container>
      )}
    </Box>
  );
};

const SubscriptionPlansContainer: React.FC = () => {
  const navigate = useNavigate();
  const venueId = useVenueId();

  React.useEffect(() => {
    if (enableShopifyVenueID && String(venueId) === String(enableShopifyVenueID)) {
      navigate('/');
    }
  }, [venueId, navigate]);

  return (
    <SubscriptionPrivider>
      <SubscriptionPlans />
    </SubscriptionPrivider>
  );
};

export default SubscriptionPlansContainer;
